import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { MiniChatHeaderProps } from '@frontend/mini-chat-store';
import { getFullName } from '@frontend/name';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { ThreadHeaderContext } from '@frontend/thread-header-context';
import { theme } from '@frontend/theme';
import { Dot, Text, Heading, styles, Button } from '@frontend/design-system';
import { AvatarSwitcher } from './avatar-switcher';
import { AiAssistantButton } from './components/header-components/ai-assistant-button';
import { ThreadOptionsDrawer } from './thread-options-drawer';
import { SizeVariant } from './types';
import { useThreadOptionsDrawer } from './use-thread-options-drawer';

const HEIGHT = 40;
const WIDTHS: Record<SizeVariant, number> = {
  full: 320,
  minimized: 240,
  avatar: HEIGHT,
};
export const AVATAR_SIZING = css({
  minWidth: 32,
  minHeight: 32,
  width: 32,
  height: 32,
});

const tinyIconButtonStyles = (color: 'white' | 'gray', hidden?: boolean) =>
  hidden
    ? css({
        svg: {
          opacity: 0,
          visibility: 'hidden',
        },
        visibility: 'hidden',
        backgroundColor: 'transparent',
        ':hover, :focus, :active': {
          backgroundColor: 'transparent',
        },
      })
    : css([
        {
          padding: theme.spacing(0.5),
          height: 'auto',
        },
        color === 'white'
          ? {
              svg: {
                color: theme.colors.white,
              },
              backgroundColor: 'transparent',
              transition: 'background-color 200ms ease-in-out',
              ':hover, :focus': {
                backgroundColor: theme.colors.primary30,
              },
              ':active': {
                backgroundColor: theme.colors.primary70,
              },
            }
          : {
              svg: {
                color: theme.colors.neutral70,
              },
              backgroundColor: 'transparent',
              transition: 'background-color 200ms ease-in-out',
              ':hover, :focus': {
                backgroundColor: theme.colors.neutral5,
              },
              ':active': {
                backgroundColor: theme.colors.neutral10,
              },
            },
      ]);
const MotionButton = motion(Button);
const MotionDot = motion(Dot);

export const MiniChatHeader = forwardRef<HTMLDivElement, MiniChatHeaderProps>(
  (
    {
      chatId,
      groupId,
      personPhone,
      onSelectPersonPhone,
      locationPhone,
      onSelectLocationPhone,
      contextPersonId,
      unreadCount,
      sizeVariant,
      onMaximize,
      onMinimize,
      onClose,
      hideButtons = false,
      floatingShadow,
      hideHoverLabel = false,
      isMobile,
      isSelectingContextContact,
      setIsSelectingContextContact,
      threadId,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation('mini-chat');
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const drawerButtonRef = useRef<HTMLElement>();
    const { getFeatureFlagValue } = useScopedAppFlagStore();
    const inboxFusionEnabled = getFeatureFlagValue('nwx:inbox-fusion');

    const { primaryContactId, associatedContacts } = PersonsV3.PersonHooks.useAssociatedContacts({
      phoneNumber: personPhone,
      locationId: groupId,
    });
    const primaryContact = associatedContacts.find((contact) => contact.personId === primaryContactId);
    const contextContact = associatedContacts.find((contact) => contact.personId === contextPersonId);

    const heading = useMemo(() => {
      if (contextContact) {
        return getFullName(contextContact);
      }

      if (primaryContact) {
        return getFullName(primaryContact);
      }

      return formatPhoneNumber(personPhone);
    }, [contextContact, personPhone, primaryContact]);

    const threadOptionsDrawerState = useThreadOptionsDrawer({
      chatId,
      groupId,
      personPhone,
      personId: contextPersonId || primaryContactId,
      open: drawerIsOpen,
      associatedContacts,
      locationPhone,
    });

    const disableAvatarClick = !primaryContactId || sizeVariant === 'avatar' || sizeVariant === 'minimized';

    const locationPhoneValue = useMemo(() => ({ locationPhone, locationId: groupId }), [locationPhone, groupId]);

    const handlePersonPhoneChange = useCallback(
      (selection: { personPhone?: string }) => onSelectPersonPhone(selection.personPhone ?? ''),
      [onSelectPersonPhone]
    );

    const threadHeaderContextValue = useMemo(
      () => ({
        personPhone,
        locationPhone: locationPhoneValue,
        onPersonPhoneChange: handlePersonPhoneChange,
        onLocationPhoneChange: onSelectLocationPhone,
        groupId,
        variant: 'small' as const,
        associatedContacts,
        primaryContactId,
        primaryContact,
        contextContact,
        contextPersonId,
        associatedContactsDrawerIsOpen: drawerIsOpen || isSelectingContextContact,
        setAssociatedContactsDrawerIsOpen: setDrawerIsOpen,
      }),
      [
        personPhone,
        locationPhoneValue,
        handlePersonPhoneChange,
        onSelectLocationPhone,
        groupId,
        associatedContacts,
        primaryContactId,
        primaryContact,
        contextContact,
        contextPersonId,
        drawerIsOpen,
        isSelectingContextContact,
      ]
    );

    return (
      <motion.div
        initial={{
          width: isMobile ? '100%' : WIDTHS[sizeVariant],
          maxWidth: WIDTHS[sizeVariant],
          padding: theme.spacing(0.5),
          paddingRight: sizeVariant === 'avatar' ? theme.spacing(0.5) : theme.spacing(2),
          boxShadow: 'none',
        }}
        animate={{
          width: isMobile ? '100%' : WIDTHS[sizeVariant],
          maxWidth: WIDTHS[sizeVariant],
          padding: theme.spacing(0.5),
          paddingRight: sizeVariant === 'avatar' ? theme.spacing(0.5) : theme.spacing(2),
          boxShadow: sizeVariant === 'full' || floatingShadow ? `0 4px 6px -2px ${theme.colors.primary90}4C` : 'none',
          backgroundColor: unreadCount ? theme.colors.primary50 : theme.colors.white,
        }}
        // The design calls for buttons inside of a button which is invalid HTML, so this is the workaround
        role={sizeVariant !== 'full' ? 'button' : undefined}
        css={[
          styles.truncate,
          {
            transition: 'background-color 200ms ease-in-out, color 200ms ease-in-out, font-weight 200ms ease-in-out',
            '.mini-chat-header-not-drawer': {
              color: unreadCount ? theme.font.colors.white : theme.font.colors.default,
              fontWeight: unreadCount ? theme.font.weight.bold : theme.font.weight.regular,
            },
            borderTopLeftRadius: theme.borderRadius.medium,
            borderTopRightRadius: theme.borderRadius.medium,
            display: 'flex',
            alignItems: 'center',
            minHeight: HEIGHT,
            height: 'auto',
            zIndex: theme.zIndex.high,
            gap: theme.spacing(1),
            overflow: 'visible',
            position: 'relative',
          },
          sizeVariant !== 'full'
            ? {
                cursor: 'pointer',
                ':focus, :hover': {
                  backgroundColor: unreadCount ? theme.colors.primary60 : theme.colors.neutral5,
                },
              }
            : {
                cursor: 'grab',
              },
        ]}
        ref={ref}
        data-testid='mini-chat-header'
        {...rest}
      >
        <AnimatePresence>
          <motion.div
            initial={{
              width: 0,
              opacity: 0,
            }}
            animate={{
              width: 'auto',
              opacity: 1,
            }}
            exit={{
              width: 0,
              opacity: 0,
            }}
          >
            <AvatarSwitcher
              key={`${chatId}-${primaryContact?.personId ?? 'primary'}-avatar`}
              personId={primaryContact?.personId}
              data-testid='mini-chat-header-avatar'
              data-value={primaryContact?.personId ?? heading}
              disableClick={
                (contextContact && contextContact?.personId !== primaryContact?.personId) || disableAvatarClick
              }
              firstName={primaryContact?.preferredName || primaryContact?.firstName}
              lastName={primaryContact?.lastName}
              locationId={groupId}
              onClick={
                isMobile && !contextContact && contextPersonId !== primaryContactId
                  ? (e) => {
                      e?.currentTarget.blur();
                      e?.stopPropagation();
                      onMinimize();
                    }
                  : () => {
                      threadOptionsDrawerState.setContextContactId('');
                    }
              }
              name={primaryContact ? getFullName(primaryContact) : undefined}
              title={
                sizeVariant === 'avatar'
                  ? t('Open Conversation with {{nameOrNumber}}', { nameOrNumber: heading })
                  : primaryContact
                  ? getFullName(primaryContact)
                  : heading
              }
              css={AVATAR_SIZING}
              disableHoverLabel={hideHoverLabel}
            />
          </motion.div>
          {!!contextContact && contextContact.personId !== primaryContact?.personId && (
            <AvatarSwitcher
              key={`${chatId}-${contextContact.personId}-context-avatar`}
              personId={contextContact.personId}
              data-testid='mini-chat-header-avatar'
              data-value={contextContact.personId}
              disableClick={disableAvatarClick}
              firstName={contextContact.preferredName || contextContact.firstName}
              lastName={contextContact.lastName}
              locationId={groupId}
              onClick={
                isMobile
                  ? (e) => {
                      e?.currentTarget.blur();
                      e?.stopPropagation();
                      onMinimize();
                    }
                  : undefined
              }
              name={getFullName(contextContact)}
              title={t('{{primaryContactName}} (Phone Owner)\n{{contextContactName}}', {
                primaryContactName: primaryContact ? getFullName(primaryContact) : '',
                contextContactName: getFullName(contextContact),
              }).trim()}
              css={[AVATAR_SIZING, contextPersonId !== primaryContactId && { marginLeft: theme.spacing(-2) }]}
              disableHoverLabel={hideHoverLabel}
            />
          )}
          {sizeVariant !== 'avatar' && (
            <motion.div
              key='heading-wrapper'
              initial={{
                width: 'auto',
              }}
              exit={{
                width: 0,
              }}
              css={[{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }, styles.truncate]}
              className='mini-chat-header-not-drawer'
            >
              <AnimatePresence>
                <Heading
                  level={3}
                  color={unreadCount ? 'white' : 'default'}
                  css={{
                    fontSize: theme.fontSize(16),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  data-testid='mini-chat-header-heading-text'
                >
                  {heading}
                </Heading>
                {sizeVariant === 'full' && threadOptionsDrawerState.hideOptions.length < 2 && (
                  <motion.div
                    key='drawer-button-wrapper'
                    exit={{
                      width: 0,
                    }}
                    css={[
                      inboxFusionEnabled && { display: 'flex' },
                      {
                        overflow: 'hidden',
                        '*': {
                          opacity: hideButtons ? 0 : 1,
                          visibility: hideButtons ? 'hidden' : undefined,
                        },
                      },
                    ]}
                    layout
                    className='mini-chat-header-not-drawer'
                  >
                    {inboxFusionEnabled && (
                      <AiAssistantButton threadId={threadId} groupId={groupId} locationPhone={locationPhone} />
                    )}
                    <MotionButton
                      variant='tertiary'
                      size='small'
                      iconName='more-small'
                      hoverLabel={drawerIsOpen ? t('Close Thread Details') : t('Thread Details')}
                      css={tinyIconButtonStyles(!!unreadCount ? 'white' : 'gray', hideButtons)}
                      onClick={(e) => {
                        e.currentTarget.blur();
                        e.stopPropagation();
                        setDrawerIsOpen((prev) => !prev);
                      }}
                      ref={(node) => (drawerButtonRef.current = (node as HTMLElement | null) ?? undefined)}
                      data-testid='mini-chat-header-drawer-button'
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
          {sizeVariant !== 'avatar' && (
            <motion.div
              initial={{
                width: 'auto',
              }}
              exit={{
                width: 0,
              }}
              css={{
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
              }}
              layout
              className='mini-chat-header-not-drawer'
            >
              <AnimatePresence>
                {!!unreadCount && (
                  <MotionDot
                    key='unread-count-dot'
                    css={{
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'static',
                      marginRight: theme.spacing(0.5),
                      minHeight: 16,
                      minWidth: 16,
                    }}
                    color='critical'
                    layout
                  >
                    <Text color='white' size='small' weight='bold'>
                      {unreadCount}
                    </Text>
                  </MotionDot>
                )}

                {sizeVariant === 'full' && (
                  <motion.div
                    key='conditional-buttons-wrapper'
                    exit={{
                      width: 0,
                    }}
                    css={{
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      '*': {
                        opacity: hideButtons ? 0 : 1,
                        visibility: hideButtons ? 'hidden' : undefined,
                      },
                    }}
                    layout
                  >
                    <MotionButton
                      variant='tertiary'
                      key='maximize-button'
                      iconName='pop-in'
                      onClickCapture={(e) => {
                        e.currentTarget.blur();
                        e.stopPropagation();
                        onMaximize();
                      }}
                      hoverLabel={t('Open in Inbox')}
                      css={tinyIconButtonStyles(!!unreadCount ? 'white' : 'gray', hideButtons)}
                      size='small'
                      layout
                      trackingId='chat-2.0-popup-inbox-maximize'
                    />
                    <MotionButton
                      variant='tertiary'
                      key='minimize-button'
                      iconName='minus'
                      onClickCapture={(e) => {
                        e.currentTarget.blur();
                        e.stopPropagation();
                        onMinimize();
                      }}
                      hoverLabel={t('Minimize')}
                      css={tinyIconButtonStyles(!!unreadCount ? 'white' : 'gray', hideButtons)}
                      layout
                      trackingId='chat-2.0-popup-inbox-minimize'
                    />
                  </motion.div>
                )}
                <MotionButton
                  variant='tertiary'
                  key='close-button'
                  iconName='x'
                  onClick={(e) => {
                    e.currentTarget.blur();
                    e.stopPropagation();
                    onClose();
                  }}
                  hoverLabel={t('Close')}
                  css={tinyIconButtonStyles(!!unreadCount ? 'white' : 'gray', hideButtons)}
                  size='small'
                  layout
                  trackingId='chat-2.0-popup-inbox-close'
                />
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
        <ThreadHeaderContext.Provider value={threadHeaderContextValue}>
          <ThreadOptionsDrawer
            {...threadOptionsDrawerState}
            groupId={groupId}
            threadId={threadId}
            locationPhone={locationPhone}
            selectedMenu={
              isSelectingContextContact ? 'associated-contacts-template' : threadOptionsDrawerState.selectedMenu
            }
            onSelectPersonPhone={onSelectPersonPhone}
            onSelectLocationPhone={onSelectLocationPhone}
            open={drawerIsOpen || isSelectingContextContact}
            onClose={() => {
              setDrawerIsOpen(false);
              setIsSelectingContextContact(false);
            }}
            associatedContacts={associatedContacts}
            closeButtonRef={drawerButtonRef}
            primaryContactId={primaryContactId}
          />
        </ThreadHeaderContext.Provider>
      </motion.div>
    );
  }
);
MiniChatHeader.displayName = 'MiniChatHeader';
